import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  Divider,
  Chip,
  Box,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
} from "@mui/material";
import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import CommonLoader from "../common/CommonLoader";
import { BatchDuplicateBottleOrders as BatchDuplicateBottleOrdersService } from "../../services/BottleOrdersService";
import { calculateScheduledOrderCount } from "./bottleOrderFormHelpers";

const scheduleOptions = [
  { value: "Once", label: "Once" },
  { value: "Weekly", label: "Weekly" },
  { value: "Monthly", label: "Monthly" },
  { value: "Quarterly", label: "Quarterly" },
  { value: "Biannual", label: "Biannual" },
  { value: "Yearly", label: "Yearly" },
];

const getCurrentDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getEndOfYearDateTime = () => {
  const now = new Date();
  return `${now.getFullYear()}-12-31`;
};

const calculateOrderCount = (startDateTime, endDateTime, scheduleFrequency) =>
  calculateScheduledOrderCount(startDateTime, endDateTime, scheduleFrequency, true);

const BatchDuplicateBottleOrders = ({ open, onClose, selectedOrders = [], onSuccess }) => {
  const [scheduleData, setScheduleData] = useState({
    startDateTime: getCurrentDateTime(),
    endDateTime: getEndOfYearDateTime(),
    scheduleFrequency: "Once",
  });
  const [loading, setLoading] = useState(false);
  const [createdOrders, setCreatedOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChange = (field) => (event) => {
    setScheduleData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isFormValid = () =>
    scheduleData.startDateTime !== "" &&
    scheduleData.endDateTime !== "" &&
    scheduleData.scheduleFrequency !== "";

  const validateForm = () => {
    if (!scheduleData.startDateTime) {
      NotificationManager.error("Start Date is required.");
      return false;
    }
    if (!scheduleData.endDateTime) {
      NotificationManager.error("End Date is required.");
      return false;
    }
    const startDate = new Date(scheduleData.startDateTime);
    const endDate = new Date(scheduleData.endDateTime);
    if (endDate <= startDate) {
      NotificationManager.error("End Date must be after Start Date.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Single API call — all source order numbers + schedule params together
      const payload = {
        sourceOrderNumbers: selectedOrders.map((o) => o.orderNumber),
        startDateTime: scheduleData.startDateTime,
        endDateTime: scheduleData.endDateTime,
        scheduleFrequency: scheduleData.scheduleFrequency,
      };

      const response = await BatchDuplicateBottleOrdersService(payload);

      if (response?.results?.createdOrders?.length > 0) {
        NotificationManager.success(response.message);
        setCreatedOrders(response.results.createdOrders);

        if (response.results.failedSourceOrderNumbers?.length > 0) {
          NotificationManager.warning(
            `Could not process source order(s): ${response.results.failedSourceOrderNumbers.join(", ")}`
          );
        }

        onSuccess?.();
      } else {
        NotificationManager.error(response?.message || "Failed to duplicate orders.");
      }
    } catch (error) {
      NotificationManager.error(
        error.response?.data?.message || error.message || "Failed to duplicate bottle orders."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setScheduleData({
      startDateTime: getCurrentDateTime(),
      endDateTime: getEndOfYearDateTime(),
      scheduleFrequency: "Once",
    });
    setCreatedOrders([]);
    setPage(0);
    onClose();
  };

  const orderCount = calculateOrderCount(
    scheduleData.startDateTime,
    scheduleData.endDateTime,
    scheduleData.scheduleFrequency
  );
  const totalExpected = orderCount * selectedOrders.length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Batch Duplicate Bottle Orders</DialogTitle>

      <DialogContent>
        <CommonLoader loading={loading} />

        {createdOrders.length > 0 ? (
          // Results view
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: "success.main", mb: 2 }}>
              Orders Duplicated Successfully!
            </Typography>

            <Paper>
              <TableContainer sx={{ maxHeight: 380, overflowY: "auto" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Order Number</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Account</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Due Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Order Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {createdOrders
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((order) => (
                        <TableRow
                          key={order.orderNumber}
                          hover
                          sx={{ "&:nth-of-type(odd)": { backgroundColor: "rgba(0,0,0,0.04)" } }}
                        >
                          <TableCell>{order.orderNumber}</TableCell>
                          <TableCell>{order.clientName}</TableCell>
                          <TableCell>{order.accountNumber}</TableCell>
                          <TableCell>
                            {order.dueDate ? new Date(order.dueDate).toLocaleDateString() : ""}
                          </TableCell>
                          <TableCell>
                            {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : ""}
                          </TableCell>
                          <TableCell>{order.shippingStatus}</TableCell>
                          <TableCell>
                            <a
                              href={`/vlp/bottle-orders/edit/${order.orderNumber}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                                textDecoration: "none",
                                color: "inherit",
                              }}
                            >
                              Edit <OpenInNewIcon fontSize="small" />
                            </a>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={createdOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Paper>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
              {createdOrders.length} order(s) created successfully
            </Typography>
          </Box>
        ) : (
          // Form view
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Duplicating {selectedOrders.length} order(s):
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2 }}>
              {selectedOrders.map((o) => (
                <Chip
                  key={o.orderNumber}
                  label={`#${o.orderNumber}${o.shippingName ? ` — ${o.shippingName}` : ""}`}
                  size="small"
                  variant="outlined"
                />
              ))}
            </Box>

            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              Schedule Information
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Start Date"
                  type="date"
                  value={scheduleData.startDateTime}
                  onChange={handleChange("startDateTime")}
                  variant="outlined"
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="End Date"
                  type="date"
                  value={scheduleData.endDateTime}
                  onChange={handleChange("endDateTime")}
                  variant="outlined"
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Schedule Frequency"
                  select
                  value={scheduleData.scheduleFrequency}
                  onChange={handleChange("scheduleFrequency")}
                  variant="outlined"
                  required
                >
                  {scheduleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Order Summary */}
            {isFormValid() && orderCount > 0 && (
              <Box
                sx={{
                  p: 2,
                  backgroundColor: "info.lighter",
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "info.light",
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "info.main", mb: 1 }}>
                  Duplication Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Frequency
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {scheduleData.scheduleFrequency}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Orders Selected
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {selectedOrders.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Occurrences Each
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {orderCount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="text.secondary">
                      Total New Orders
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                      {totalExpected}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          {createdOrders.length > 0 ? "Close" : "Cancel"}
        </Button>
        {createdOrders.length === 0 && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={loading || !isFormValid()}
          >
            {loading ? "Duplicating..." : `Duplicate ${selectedOrders.length} Order(s)`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BatchDuplicateBottleOrders;